import { useCallback, useEffect, useMemo, useState } from 'react';
import { io } from 'socket.io-client';
import {
  getGradeLabel,
  getPermissionLabel,
  getStaffName,
  getStaffTitle,
  parseAssignedGradeSplitIds,
  parseAssignedGrades,
} from '@car-pool/shared/schoolRecords';
import { apiFetch, SOCKET_URL } from '../config/api';
import ResetCodeButton from '../components/ResetCodeButton';
import SendStaffOnboardingEmailButton from '../components/SendStaffOnboardingEmailButton';
import SendStaffOnboardingWhatsAppButton from '../components/SendStaffOnboardingWhatsAppButton';
import SchoolAvatar from '../components/SchoolAvatar';
import StaffAccessLevelCard from '../components/StaffAccessLevelCard';
import StaffRequestCard from '../components/StaffRequestCard';
import SchoolDirectoryRow from '../components/SchoolDirectoryRow';
import SchoolInfoRow from '../components/SchoolInfoRow';
import SchoolLayout from '../components/SchoolLayout';
import { getHostedAppUrls } from '../config/hostUrls';
import { SUPER_ADMIN_PERMISSION, isSchoolAdminUser } from '../lib/adminAccess';

const ONBOARDING_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'onboarded', label: 'Onboarded' },
  { key: 'not-onboarded', label: 'Not onboarded' },
];

function getStaffBadge(staff) {
  if (!staff?.is_active) {
    return { label: 'Inactive account', tone: 'muted', rank: 3 };
  }

  const assignedGrades = parseAssignedGrades(staff.assigned_grades);
  const assignedGradeSplitIds = parseAssignedGradeSplitIds(staff.assigned_grade_split_ids);

  if (staff.permission_level === 'TEACHER') {
    const assignedGroupCount = assignedGrades.length + assignedGradeSplitIds.length;
    if (assignedGroupCount > 0) {
      return {
        label: `${assignedGroupCount} group${assignedGroupCount === 1 ? '' : 's'} assigned`,
        tone: 'success',
        rank: 0,
      };
    }

    return {
      label: 'Teacher without group coverage',
      tone: 'warning',
      rank: 1,
    };
  }

  return {
    label: 'School-wide access',
    tone: 'info',
    rank: 2,
  };
}

function compareStaff(left, right) {
  if (Boolean(left.is_active) !== Boolean(right.is_active)) {
    return left.is_active ? -1 : 1;
  }

  const leftName = `${left.l_name || ''} ${left.f_name || ''}`.trim().toLowerCase();
  const rightName = `${right.l_name || ''} ${right.f_name || ''}`.trim().toLowerCase();
  return leftName.localeCompare(rightName);
}

function getAccessLevel(permissionLevel) {
  return permissionLevel === 'TEACHER' ? 'viewer' : 'manager';
}

function getPermissionForAccessLevel(accessLevel, currentPermissionLevel) {
  if (accessLevel === 'viewer') return 'TEACHER';
  if (currentPermissionLevel === 'SUPER_ADMIN/SUPPORT') return 'SUPER_ADMIN/SUPPORT';
  return 'ADMIN';
}

function formatPhone(value) {
  if (!value) return 'No phone number provided';
  return value.startsWith('+') ? value : `+ ${value}`;
}

function formatLastLogin(value) {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Never';
  return date.toLocaleString([], {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function formatStaffRequest(request) {
  const name = [request.f_name, request.l_name].filter(Boolean).join(' ').trim() || 'Staff request';
  const requestedAccess = getPermissionLabel(request.requested_permission_level || 'TEACHER');

  return {
    id: request.id,
    name,
    phone: formatPhone(request.mobile_phone),
    email: request.school_email || 'No email provided',
    role: request.position_title ? `${request.position_title} · ${requestedAccess}` : requestedAccess,
    schoolName: request.school_name || 'School',
    seed: request.id,
    createdAt: request.created_at,
  };
}

export default function StaffList({ token, navigate, schoolId, user, selectStaffId = null, bare = false }) {
  const [staff, setStaff] = useState([]);
  const [gradeNames, setGradeNames] = useState([]);
  const [gradeSplits, setGradeSplits] = useState([]);
  const [staffRequests, setStaffRequests] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState(selectStaffId || null);

  useEffect(() => {
    if (selectStaffId) setSelectedStaffId(selectStaffId);
  }, [selectStaffId]);
  const [updatingAccess, setUpdatingAccess] = useState(false);
  const [requestActionId, setRequestActionId] = useState(null);
  const [copyStatus, setCopyStatus] = useState('');
  const [requestNotice, setRequestNotice] = useState('');
  const [search, setSearch] = useState('');
  const [onboardingFilter, setOnboardingFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const schoolScopedUser = isSchoolAdminUser(user);
  const staffRegistrationLink = useMemo(() => (
    `${getHostedAppUrls().webapp}/staff-register/${schoolId}`
  ), [schoolId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [staffRes, gradeNamesRes, gradeSplitsRes, requestsRes] = await Promise.all([
        apiFetch(`/staff?school_id=${schoolId}`, {}, token),
        apiFetch(`/school-grade-names?school_id=${schoolId}`, {}, token).catch(() => ({ data: [] })),
        apiFetch(`/school-grade-splits?school_id=${schoolId}&is_active=true`, {}, token).catch(() => ({ data: [] })),
        apiFetch(`/staff/registration-requests?school_id=${schoolId}&status=pending`, {}, token).catch(() => ({ data: [] })),
      ]);

      const nextStaff = staffRes.data || [];

      setStaff(nextStaff);
      setGradeNames((gradeNamesRes.data || []).filter((grade) => grade.is_active));
      setGradeSplits((gradeSplitsRes.data || []).filter((split) => split.is_active));
      setStaffRequests((requestsRes.data || []).map(formatStaffRequest));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [schoolId, token]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!schoolId) return undefined;

    const socket = io(SOCKET_URL);
    socket.on('connect', () => {
      socket.emit('watch-school', schoolId);
    });
    socket.on('staff-registration-request-created', (request) => {
      if (Number(request.school_id) !== Number(schoolId)) return;
      setStaffRequests((current) => {
        if (current.some((item) => Number(item.id) === Number(request.id))) return current;
        return [formatStaffRequest(request), ...current];
      });
    });

    return () => socket.disconnect();
  }, [schoolId]);

  const gradeLabelMap = useMemo(() => {
    const entries = gradeNames.map((grade) => [Number(grade.grade_code), grade.label]);
    return Object.fromEntries(entries);
  }, [gradeNames]);

  const sortedStaff = useMemo(
    () => [...staff].sort(compareStaff),
    [staff]
  );

  const onboardingCounts = useMemo(() => {
    const onboarded = sortedStaff.filter((member) => member.has_password).length;
    return {
      all: sortedStaff.length,
      onboarded,
      'not-onboarded': sortedStaff.length - onboarded,
    };
  }, [sortedStaff]);

  const filteredStaff = useMemo(() => {
    const query = search.trim().toLowerCase();

    return sortedStaff.filter((member) => {
      if (onboardingFilter === 'onboarded' && !member.has_password) return false;
      if (onboardingFilter === 'not-onboarded' && member.has_password) return false;
      if (!query) return true;

      const fields = [
        member.f_name,
        member.l_name,
        member.school_email,
        member.mobile_phone,
        member.position_title,
        getPermissionLabel(member.permission_level),
      ];

      return fields.some((field) => String(field || '').toLowerCase().includes(query));
    });
  }, [onboardingFilter, search, sortedStaff]);

  useEffect(() => {
    if (loading) return;

    if (!filteredStaff.length) {
      setSelectedStaffId(null);
      return;
    }

    const selectedStillExists = filteredStaff.some((member) => member.id === selectedStaffId);
    if (!selectedStillExists) {
      setSelectedStaffId(filteredStaff[0].id);
    }
  }, [loading, filteredStaff, search, selectedStaffId, sortedStaff]);

  const selectedStaff = useMemo(
    () => sortedStaff.find((member) => member.id === selectedStaffId) || null,
    [selectedStaffId, sortedStaff]
  );

  const selectedAssignedGrades = useMemo(
    () => parseAssignedGrades(selectedStaff?.assigned_grades),
    [selectedStaff]
  );
  const selectedAssignedGradeSplitIds = useMemo(
    () => parseAssignedGradeSplitIds(selectedStaff?.assigned_grade_split_ids),
    [selectedStaff]
  );
  const selectedAssignedGradeSplits = useMemo(() => {
    const selectedIds = new Set(selectedAssignedGradeSplitIds);
    return gradeSplits.filter((split) => selectedIds.has(Number(split.id)));
  }, [gradeSplits, selectedAssignedGradeSplitIds]);
  const selectedIsSuperAdminStaff = selectedStaff?.permission_level === SUPER_ADMIN_PERMISSION;
  const selectedStaffLocked = schoolScopedUser && selectedIsSuperAdminStaff;

  const handleDeleteAccount = async (member) => {
    const fullName = getStaffName(member);
    if (!window.confirm(`Delete staff account "${fullName}"? This will deactivate the account and prevent login.`)) return;

    try {
      await apiFetch(`/staff/${member.id}`, { method: 'DELETE' }, token);
      setStaff((previous) => previous.map((item) => (
        item.id === member.id
          ? { ...item, is_active: 0 }
          : item
      )));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAccessLevelChange = async (member, accessLevel) => {
    if (!member) return;
    if (schoolScopedUser && member.permission_level === SUPER_ADMIN_PERMISSION) {
      setError('Only super admins can edit support administrator accounts.');
      return;
    }

    const nextPermission = getPermissionForAccessLevel(accessLevel, member.permission_level);
    if (nextPermission === member.permission_level) return;

    setUpdatingAccess(true);
    setError('');

    try {
      await apiFetch(`/staff/${member.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          school_id: member.school_id,
          f_name: member.f_name,
          l_name: member.l_name,
          school_email: member.school_email || null,
          mobile_phone: member.mobile_phone,
          position_title: member.position_title || null,
          permission_level: nextPermission,
          is_active: Number(Boolean(member.is_active)),
          assigned_grades: nextPermission === 'TEACHER'
            ? parseAssignedGrades(member.assigned_grades)
            : null,
          assigned_grade_split_ids: nextPermission === 'TEACHER'
            ? parseAssignedGradeSplitIds(member.assigned_grade_split_ids)
            : null,
        }),
      }, token);

      setStaff((current) => current.map((item) => (
        item.id === member.id
          ? {
            ...item,
            permission_level: nextPermission,
            assigned_grades: nextPermission === 'TEACHER' ? item.assigned_grades : null,
            assigned_grade_split_ids: nextPermission === 'TEACHER' ? item.assigned_grade_split_ids : null,
          }
          : item
      )));
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingAccess(false);
    }
  };

  const handleCopyRegistrationLink = async () => {
    setError('');

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(staffRegistrationLink);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = staffRegistrationLink;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      setCopyStatus('Registration link copied.');
      setTimeout(() => setCopyStatus(''), 2200);
    } catch {
      setError('Unable to copy the registration link. Please copy it manually.');
    }
  };

  const handleApproveRequest = async (request) => {
    if (!request) return;
    setRequestActionId(request.id);
    setError('');
    setRequestNotice('');

    try {
      const res = await apiFetch(`/staff/registration-requests/${request.id}/approve`, { method: 'POST' }, token);
      const newStaff = res.data?.staff || null;

      if (newStaff) {
        setStaff((current) => [...current.filter((member) => member.id !== newStaff.id), newStaff]);
        setSelectedStaffId(newStaff.id);
      }
      setStaffRequests((current) => current.filter((item) => item.id !== request.id));
      setRequestNotice(`${request.name} was approved and can now sign in.`);
    } catch (err) {
      setError(err.message || 'Unable to approve staff request.');
    } finally {
      setRequestActionId(null);
    }
  };

  const handleRejectRequest = async (request) => {
    if (!request) return;
    if (!window.confirm(`Reject ${request.name}'s staff account request?`)) return;

    setRequestActionId(request.id);
    setError('');
    setRequestNotice('');

    try {
      await apiFetch(`/staff/registration-requests/${request.id}/reject`, { method: 'POST' }, token);
      setStaffRequests((current) => current.filter((item) => item.id !== request.id));
      setRequestNotice(`${request.name}'s request was rejected.`);
    } catch (err) {
      setError(err.message || 'Unable to reject staff request.');
    } finally {
      setRequestActionId(null);
    }
  };

  const content = (
    <div className="sap-page">
        {error && <div className="sa-error sap-inline-error">{error}</div>}

        {loading ? (
          <div className="sa-empty">Loading...</div>
        ) : (
          <div className="sat-staff-shell">
            <section className="sap-parents-panel sap-directory-panel">
              <div className="sap-panel-heading">
                <div>
                  <h2>Staff</h2>
                  <p>View, manage, and track all school staff and access coverage</p>
                </div>
                <button className="sa-btn sa-btn-primary" onClick={() => navigate('staff-create', schoolId)}>
                  + Add Staff
                </button>
              </div>

              <div className="sap-directory-list">
                {filteredStaff.length === 0 ? (
                  <div className="sa-empty sap-compact-empty">
                    {sortedStaff.length === 0 ? 'No staff members found for this school.' : 'No staff members match your search.'}
                  </div>
                ) : (
                  filteredStaff.map((member) => {
                    const fullName = getStaffName(member);
                    const badge = getStaffBadge(member);

                    return (
                      <SchoolDirectoryRow
                        key={member.id}
                        record={member}
                        name={fullName}
                        meta={`${member.has_password ? 'Onboarded' : 'Not onboarded'} - ${getStaffTitle(member)}`}
                        className="sat-directory-row"
                        active={selectedStaff?.id === member.id}
                        onClick={() => setSelectedStaffId(member.id)}
                      >
                        <span className={`sat-status-pill ${badge.tone}`}>{badge.label}</span>
                      </SchoolDirectoryRow>
                    );
                  })
                )}
              </div>

              <div className="sap-directory-footer">
                <div className="sap-onboarding-filter" role="group" aria-label="Staff onboarding filter">
                  {ONBOARDING_FILTERS.map((filter) => (
                    <button
                      key={filter.key}
                      type="button"
                      className={`sap-onboarding-filter-btn ${onboardingFilter === filter.key ? 'active' : ''}`}
                      onClick={() => setOnboardingFilter(filter.key)}
                    >
                      <span>{filter.label}</span>
                      <strong>{onboardingCounts[filter.key]}</strong>
                    </button>
                  ))}
                </div>
                <label className="sap-directory-search">
                  <span>⌕</span>
                  <input
                    type="text"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search"
                  />
                </label>
              </div>
            </section>

            <section className="sap-parents-panel sap-detail-panel">
              <div className="sap-panel-heading">
                <div>
                  <h2>Detailed view</h2>
                  <p>Manage and view staff in detail</p>
                </div>
              </div>

              {selectedStaff ? (
                <>
                  <div className="sat-staff-hero">
                    <SchoolAvatar
                      name={getStaffName(selectedStaff)}
                      seed={selectedStaff.id}
                      photoPath={selectedStaff.photo_relative_path}
                      enableViewer
                      large
                    />
                    <h3>{getStaffName(selectedStaff)}</h3>
                    <span>{getStaffTitle(selectedStaff)}</span>
                    <span>{selectedStaff.school_email || 'No school email'}</span>
                    <span>{selectedStaff.mobile_phone || 'No mobile phone'}</span>
                  </div>

                  <div className="sap-section-card">
                    <div className="sap-section-caption">Role &amp; Access</div>
                    <SchoolInfoRow label="Permission" value={getPermissionLabel(selectedStaff.permission_level)} />
                    <SchoolInfoRow label="Status" value={selectedStaff.is_active ? 'Active' : 'Inactive'} />
                    <SchoolInfoRow label="Position" value={selectedStaff.position_title || 'Not set'} />
                  </div>

                  <StaffAccessLevelCard
                    activeLevel={getAccessLevel(selectedStaff.permission_level)}
                    disabled={updatingAccess || selectedStaffLocked}
                    onSelect={(accessLevel) => handleAccessLevelChange(selectedStaff, accessLevel)}
                  />

                  <div className="sap-section-card">
                    <div className="sap-section-caption">Assigned Groups</div>
                    {selectedAssignedGrades.length === 0 && selectedAssignedGradeSplits.length === 0 ? (
                      <div className="sap-section-empty sat-grade-empty">
                        {selectedStaff.permission_level === 'TEACHER'
                          ? 'This teacher is not assigned to any grades or splits yet.'
                          : 'This role has school-wide access and no group restrictions.'}
                      </div>
                    ) : (
                      <div className="sat-grade-chip-list">
                        {selectedAssignedGrades.map((grade) => (
                          <span key={grade} className="sat-grade-chip">
                            {getGradeLabel(grade, gradeLabelMap)}
                          </span>
                        ))}
                        {selectedAssignedGradeSplits.map((split) => (
                          <span key={`split-${split.id}`} className="sat-grade-chip">
                            {split.split_code}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="sap-section-card">
                    <div className="sap-section-caption">Contact Information</div>
                    <SchoolInfoRow label="Email" value={selectedStaff.school_email} />
                    <SchoolInfoRow label="Phone" value={selectedStaff.mobile_phone} />
                    <SchoolInfoRow label="Name" value={getStaffName(selectedStaff)} />
                  </div>

                  <div className="sap-section-card">
                    <div className="sap-section-caption">Misc</div>
                    <SchoolInfoRow label="Onboarding" value={selectedStaff.has_password ? 'Onboarded' : 'Not onboarded'} />
                    <SchoolInfoRow label="Last Login" value={formatLastLogin(selectedStaff.last_login_at)} />
                  </div>

                  <div className="sap-parent-actions">
                    {selectedStaffLocked ? (
                      <div className="sap-section-empty">Only super admins can edit support administrator accounts.</div>
                    ) : (
                      <>
                        <button
                          className="sa-btn sa-btn-secondary"
                          onClick={() => navigate('staff-edit', { staff: selectedStaff, schoolId })}
                        >
                          Edit Staff
                        </button>
                        <ResetCodeButton
                          token={token}
                          userType="staff"
                          userId={selectedStaff.id}
                          userName={getStaffName(selectedStaff)}
                        />
                        <SendStaffOnboardingEmailButton
                          token={token}
                          staffId={selectedStaff.id}
                          schoolId={schoolId}
                          hasPassword={selectedStaff.has_password}
                        />
                        <SendStaffOnboardingWhatsAppButton
                          token={token}
                          staffId={selectedStaff.id}
                          schoolId={schoolId}
                          hasPassword={selectedStaff.has_password}
                        />
                        {selectedStaff.is_active ? (
                          <button
                            className="sa-btn sa-btn-danger"
                            onClick={() => handleDeleteAccount(selectedStaff)}
                          >
                            Delete Account
                          </button>
                        ) : null}
                      </>
                    )}
                  </div>
                </>
              ) : (
                <div className="sa-empty sap-compact-empty">Select a staff member to view details.</div>
              )}
            </section>

            <section className="sap-parents-panel sat-requests-panel">
              <div className="sap-panel-heading">
                <div>
                  <h2>Staff Requests</h2>
                  <p>Review and approve staff account access</p>
                </div>
              </div>

              <div className="sat-registration-link-card">
                <div>
                  <strong>Staff registration link</strong>
                  <span>{staffRegistrationLink}</span>
                </div>
                <button type="button" className="sa-btn sa-btn-secondary" onClick={handleCopyRegistrationLink}>
                  {copyStatus ? 'Copied' : 'Copy Staff Registration Link'}
                </button>
              </div>
              {requestNotice ? <div className="sa-success sat-request-notice">{requestNotice}</div> : null}

              <div className="sat-request-list">
                {staffRequests.length === 0 ? (
                  <div className="sap-section-card">
                    <div className="sap-section-empty">No pending staff requests right now.</div>
                  </div>
                ) : (
                  staffRequests.map((request) => (
                    <StaffRequestCard
                      key={request.id}
                      request={request}
                      disabled={requestActionId === request.id}
                      onDeny={() => handleRejectRequest(request)}
                      onApprove={() => handleApproveRequest(request)}
                    />
                  ))
                )}
              </div>
            </section>
          </div>
        )}
      </div>
  );

  if (bare) return content;

  return (
    <SchoolLayout
      token={token}
      navigate={navigate}
      schoolId={schoolId}
      user={user}
      activePage="staff"
      breadcrumb="Staff"
    >
      {content}
    </SchoolLayout>
  );
}
